// WP-1.14 (OB-4, S-13 `/accounts/:id/reconcile`): 3-step reconciliation wizard — ① enter the
// real balance now → drift ② guided hunt: candidates, each drill-linked to where it's fixed
// (not edited inline in this wizard — a scope decision, noted in the changelog) ③ if drift
// remains, record an Adjustment (reason required, amount prefilled — TX-1). Exit always
// summarizes what changed.
import {
  fromMinor,
  parseAmountInput,
  toDecimalString,
  type ReconcileCandidate,
} from '@pfos/engine';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { AmountField, Field, inputClass, primaryButtonInlineClass } from '../components/FormKit';
import { useAccount } from '../lib/accounts';
import { ApiError } from '../lib/api';
import { useRecordAdjustment, useReconciliation } from '../lib/reconcile';

const CANDIDATE_LABEL: Record<ReconcileCandidate['kind'], string> = {
  uncategorized: 'Uncategorized transaction',
  duplicate: 'Suspected duplicate',
  stale_in_transit: 'Stale in-transit transfer',
  day_gap: 'Gap in activity',
};

function candidateKey(candidate: ReconcileCandidate): string {
  switch (candidate.kind) {
    case 'uncategorized':
    case 'duplicate':
      return `${candidate.kind}-${candidate.transactionId}`;
    case 'stale_in_transit':
      return `${candidate.kind}-${candidate.transferId}`;
    case 'day_gap':
      return `${candidate.kind}-${candidate.sinceDate}`;
  }
}

function candidateHref(candidate: ReconcileCandidate): string | undefined {
  switch (candidate.kind) {
    case 'uncategorized':
    case 'duplicate':
      return `/tx/${candidate.transactionId}`;
    case 'stale_in_transit':
      return '/tx';
    case 'day_gap':
      return undefined;
  }
}

function CandidateRow({ candidate }: { candidate: ReconcileCandidate }): React.JSX.Element {
  const href = candidateHref(candidate);
  const detail =
    candidate.kind === 'day_gap'
      ? `Since ${candidate.sinceDate} — ${String(candidate.gapDays)} days with no activity`
      : candidate.kind === 'stale_in_transit'
        ? `${String(candidate.daysStale)} days in transit`
        : candidate.at.slice(0, 10);
  return (
    <li className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-0">
      <div>
        <p className="text-sm text-ink">{CANDIDATE_LABEL[candidate.kind]}</p>
        <p className="text-xs text-muted">{detail}</p>
      </div>
      {href !== undefined && (
        <Link to={href} className="text-sm text-primary hover:underline">
          Review
        </Link>
      )}
    </li>
  );
}

export function ReconcilePage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: account } = useAccount(id);
  const [statedInput, setStatedInput] = useState('');
  const [statedBalanceMinor, setStatedBalanceMinor] = useState<number | undefined>(undefined);
  const [amountDraft, setAmountDraft] = useState('');
  const [reason, setReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: snapshot, isLoading } = useReconciliation(id, statedBalanceMinor);
  const adjustMutation = useRecordAdjustment(id);

  if (account === undefined) return <p className="text-sm text-muted">Loading…</p>;

  const showDrift = (): void => {
    setError(null);
    const parsed = parseAmountInput(statedInput, { defaultCurrency: account.currency });
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setStatedBalanceMinor(parsed.money.minorUnits);
  };

  const openAdjustment = (): void => {
    if (snapshot === undefined) return;
    setAmountDraft(toDecimalString(fromMinor(snapshot.currency, snapshot.driftMinor)));
    setReason('');
    setAdjusting(true);
  };

  const saveAdjustment = (): void => {
    setError(null);
    const parsed = parseAmountInput(amountDraft, { defaultCurrency: account.currency });
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    adjustMutation.mutate(
      { amountMinor: parsed.money.minorUnits, reason },
      {
        onSuccess: () => {
          setAdjusting(false);
          setDone(true);
        },
        onError: (e) => {
          setError(e instanceof ApiError ? e.message : 'Could not save the adjustment');
        },
      },
    );
  };

  return (
    <div>
      <button
        onClick={() => {
          navigate(`/accounts/${id}`);
        }}
        className="mb-4 flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft size={16} /> {account.name}
      </button>

      <h1 className="mb-4 text-xl font-semibold text-ink">Reconcile {account.name}</h1>

      {done && (
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-6">
          <p className="text-sm text-ink">Reconciled — the drift is now zero.</p>
          <button
            type="button"
            className={`${primaryButtonInlineClass} mt-3`}
            onClick={() => {
              navigate(`/accounts/${id}`);
            }}
          >
            Back to account
          </button>
        </div>
      )}

      {!done && (
        <>
          <div className="mb-4 max-w-sm rounded-[var(--radius-card)] border border-border bg-surface p-4">
            <Field label="Actual balance now" htmlFor="stated-balance">
              <input
                id="stated-balance"
                className={inputClass}
                value={statedInput}
                onChange={(e) => {
                  setStatedInput(e.target.value);
                }}
                inputMode="decimal"
                placeholder={`e.g. ${account.currency} 65000`}
              />
            </Field>
            <button type="button" className={primaryButtonInlineClass} onClick={showDrift}>
              Show drift
            </button>
            {error !== null && !adjusting && <p className="mt-2 text-sm text-danger">{error}</p>}
          </div>

          {isLoading && <p className="text-sm text-muted">Checking…</p>}

          {snapshot !== undefined && (
            <>
              <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
                <p className="text-xs uppercase tracking-wide text-muted">Drift</p>
                {snapshot.driftMinor === 0 ? (
                  <p className="text-sm text-ink">No drift — this account is fully reconciled.</p>
                ) : (
                  <Amount
                    currency={snapshot.currency}
                    minorUnits={snapshot.driftMinor}
                    className="text-2xl font-semibold"
                  />
                )}
              </div>

              {snapshot.candidates.length > 0 && (
                <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                    Guided hunt
                  </p>
                  <ul>
                    {snapshot.candidates.map((c) => (
                      <CandidateRow key={candidateKey(c)} candidate={c} />
                    ))}
                  </ul>
                </div>
              )}

              {snapshot.driftMinor !== 0 && !adjusting && (
                <button type="button" className={primaryButtonInlineClass} onClick={openAdjustment}>
                  Record an adjustment
                </button>
              )}

              {adjusting && (
                <div className="max-w-sm rounded-[var(--radius-card)] border border-border bg-surface p-4">
                  <p className="mb-3 text-xs text-muted">
                    Reconciliation-only; shows in Data Health.
                  </p>
                  <Field label="Amount" htmlFor="adjustment-amount">
                    <AmountField
                      id="adjustment-amount"
                      currency={snapshot.currency}
                      value={amountDraft}
                      onChange={setAmountDraft}
                    />
                  </Field>
                  <Field label="Reason" htmlFor="adjustment-reason">
                    <input
                      id="adjustment-reason"
                      className={inputClass}
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value);
                      }}
                    />
                  </Field>
                  <button
                    type="button"
                    className={primaryButtonInlineClass}
                    disabled={adjustMutation.isPending}
                    onClick={saveAdjustment}
                  >
                    Save adjustment
                  </button>
                  {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
